import { Button, FormControl, Input, Switch } from '@deckee/deck-hand';
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { captureEvent } from '../../../../../services/event-capture';
import { useAppDispatch } from '../../../../../services/store';
import { selectCurrentMember } from '../../../../../services/store/modules/backend-service';
import {
  addOrUpdateEmergencyContact,
  fetchExistingEmergencyContacts,
  selectAllEmergencyContacts,
} from '../../../../../services/store/modules/plan';
import { PlanEmergencyContact } from '../../../../../services/store/modules/plan/models';
import BasicDrawerLayout from '../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../../layouts/components/LayoutBody/LayoutBody';
import PhoneInput from '../PhoneInput/PhoneInput';
import { selectedFloatPlanLoggedOn } from '../../../../../services/store/modules/vessels';

interface AddEmergencyContactPageProps {
  contactId?: string;
}

const AddEmergencyContactPage: FC<AddEmergencyContactPageProps> = ({
  contactId,
}) => {
  const allContacts = useSelector(selectAllEmergencyContacts);
  const existingContact = allContacts.find((c) => c.id === contactId);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const member = useSelector(selectCurrentMember);
  const [onboard, setOnboard] =
    useState<PlanEmergencyContact['contactType']>('onland');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isLoggedOn = useSelector(selectedFloatPlanLoggedOn);
  const [id, setId] = useState(uuidv4());

  useEffect(() => {
    dispatch(fetchExistingEmergencyContacts());
  }, [dispatch]);

  useEffect(() => {
    if (existingContact?.id) {
      setEmail(existingContact.email);
      setName(existingContact.name);
      setContactNumber(existingContact.contactNumber);
      setId(existingContact.id);
    }
  }, [existingContact]);

  const handleAddEmergencyContact = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    captureEvent({
      type: 'plan: click add emergency contact',
      payload: {},
    });

    dispatch(
      addOrUpdateEmergencyContact({
        id,
        email,
        name,
        contactNumber,
        contactType: onboard,
        createdByMember: member.id,
        selected: false,
        updatedAt: new Date().toISOString(),
      }),
    );

    // This may need to change when editing is in place
    history.replace('/maps/plan/emergency-contacts');
  };

  const isContactNumberRequired = isLoggedOn && onboard === 'onboard';

  return (
    <BasicDrawerLayout
      heading="Add emergency contact"
      showMenuBar={false}
      onBack={history.goBack}
      onClose={null}
    >
      <LayoutBody>
        <form onSubmit={handleAddEmergencyContact}>
          <FormControl label="Full name *">
            <Input
              placeholder="Full name"
              value={name}
              aria-label="full name"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              required
            />
          </FormControl>

          <FormControl label="Email *">
            <Input
              type="email"
              placeholder="Email"
              aria-label="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              required
            />
          </FormControl>

          <FormControl
            label={`Phone number ${isContactNumberRequired ? '*' : ''}`}
          >
            <PhoneInput
              name="contactNumber"
              onChange={setContactNumber}
              value={contactNumber}
              defaultCountry="AU"
              aria-label="contact number"
              initialValueFormat="national"
              displayInitialValueAsLocalNumber
              limitMaxLength
              required={isContactNumberRequired}
            />
          </FormControl>

          <Switch.Group
            name="radio-type"
            value={onboard}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOnboard(e.target.value as PlanEmergencyContact['contactType']);
            }}
          >
            <Switch id="onland" value="onland">
              On land
            </Switch>
            <Switch id="onboard" value="onboard">
              Onboard
            </Switch>
          </Switch.Group>

          <Button
            type="submit"
            mt="10px"
            wide
            disabled={
              contactNumber ? !isValidPhoneNumber(contactNumber) : false
            }
          >
            {contactId ? 'Save' : 'Add contact to plan'}
          </Button>
        </form>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

AddEmergencyContactPage.defaultProps = {
  contactId: undefined,
};

export default AddEmergencyContactPage;
